import { useEffect, useState } from 'react';

/**
 * True on a narrow screen — phone, or a tablet held in portrait.
 *
 * Some things cannot be fixed with CSS, because they are not a styling problem:
 * a four-column kanban on a 390px screen does not need narrower columns, it
 * needs to not be a kanban. The board rendered its headings one letter per
 * line — N/O/T/S/T/A/R/T/E/D down the page — which is what a layout looks like
 * when it has run out of room and nobody told it to stop. Those decisions are
 * made in the component, so the component has to know.
 *
 * 900px is the second step of the app's four-step scale (640 / 900 / 1280 /
 * above), so CSS and behaviour agree instead of disagreeing by twenty pixels —
 * which is what happens when a card folds at 620 and the toolbar above it
 * folds at 760: between those widths something is always broken.
 *
 * It covers the tablet on purpose. Four kanban columns are no more readable on
 * a 768px iPad than on a phone.
 */
/* The second clause catches Chrome's "Desktop site" mode on a phone or
   tablet: the browser then REPORTS a desktop-width viewport (~1220px), so no
   width test can see the truth — but the pointer is still a finger. A
   coarse-pointer device under 1366px gets the compact behaviour whatever
   width its browser claims. A real laptop with a mouse is never coarse. */
const QUERY = '(max-width: 900px), ((pointer: coarse) and (max-width: 1366px))';

export function useIsPhone(): boolean {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(QUERY);
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    on();
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

/**
 * IS THE SIDEBAR COVERING THE PAGE?
 *
 * Two different questions were being answered by one hook. `useIsPhone` is
 * "does this behave like a touch device" — it deliberately includes a tablet
 * in desktop mode, because a finger is a finger. But the sidebar only becomes
 * an OVERLAY below 901px; the CSS says so itself
 * (`@media (min-width: 901px) { .sidebar-scrim { display: none } }`).
 *
 * Above that it sits beside the content and covers nothing — so closing it
 * after every navigation was not "dismissing a drawer", it was folding away a
 * rail the person had deliberately opened. Reported exactly like that: the
 * sidebar collapsed on every section, when only Actes is meant to fold it
 * (Actes carries a navigation of its own, and puts the rail back the way it
 * found it).
 *
 * This is the narrow question: is it a drawer right now?
 */
const DRAWER = '(max-width: 900px)';

export function useIsDrawer(): boolean {
  const [drawer, setDrawer] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DRAWER).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(DRAWER);
    const on = () => setDrawer(mq.matches);
    mq.addEventListener('change', on);
    on();
    return () => mq.removeEventListener('change', on);
  }, []);
  return drawer;
}
